import _get from 'lodash.get';
import { ScannedDokument } from '../components/visum-erfassung/dokumente/scanned.document';
import { AppConfig } from '../models/config/app-config';
import { DateFormatter } from './date-utils.service';
import { Marshaller } from './marshaller';
import { ValueListsService } from './value-lists.service';

export class KurzfristigerAufenthaltMarshaller extends Marshaller {

  constructor(
    valueListsService: ValueListsService,
    dateFormatter: DateFormatter,
    private appConfig: AppConfig
  ) {
    super(valueListsService, dateFormatter);
  }

  public marshall(formValue: any): any {
    const ret: any = this.marshallAlphanumericOnly(formValue);

    if (formValue.lichtbild && formValue.lichtbild.lichtbild) {
      const biometrie = this.getBiometrie(ret);
      biometrie.lichtbild = formValue.lichtbild.lichtbild;
    }

    if (formValue.fingerabdruck) {
      if (formValue.fingerabdruck.fingerabdruecke) {
        const biometrie = this.getBiometrie(ret);
        biometrie.fingerabdruecke = formValue.fingerabdruck.fingerabdruecke;
      }
      if (formValue.fingerabdruck.ausnahmegrund) {
        const biometrie = this.getBiometrie(ret);
        biometrie.fingerabdrueckeAusnahmegrund = formValue.fingerabdruck.ausnahmegrund;
      }
    }

    if (formValue.dokumente) {
      const dokumentListe: any[] = [];

      const dokumentListFromForm: ScannedDokument[] = formValue.dokumente.pdfDocuments;
      if (dokumentListFromForm) {
        dokumentListFromForm.forEach((scannedDokumentValue: ScannedDokument) => {
          dokumentListe.push({
            art: this.valueListSelection('dokument_art', scannedDokumentValue.doctype),
            dokument: scannedDokumentValue.data,
            groesseInBytes: scannedDokumentValue.sizeInBytes,
            digitialisierungsdatum: scannedDokumentValue.creationDateAndTime
          });
        });
      }

      if (dokumentListe.length > 0) {
        ret.dokumentListe = dokumentListe;
      }
    }

    return ret;
  }

  private getBiometrie(ret: any): any {
    if (!ret.antragsteller.biometrie) {
      ret.antragsteller.biometrie = {};
    }
    return ret.antragsteller.biometrie;
  }

  public marshallAlphanumericOnly(formValue: any) {

    const ret: any = {
      visumAntragsart: this.appConfig.visumAntragsartEnum,

      antragsteller: {
        familienname: this.get(formValue.angabenZurPerson.familienname),
        vorname: this.get(formValue.angabenZurPerson.vorname),
        geburtsdatum: this.get(formValue.angabenZurPerson.geburtsdatum),
        geburtsname: this.get(formValue.angabenZurPerson.geburtsname),
        geburtsort: this.get(formValue.angabenZurPerson.geburtsort),
        geburtsland: this.valueListSelection('land', formValue.angabenZurPerson.geburtsland),
        familienstand: this.valueListSelection('familienstand', formValue.angabenZurPerson.familienstand),
        geschlecht: this.valueListSelection('geschlecht', formValue.angabenZurPerson.geschlecht),
        staatsangehoerigkeitListe: this.valueListArraySelection(
          'land',
          formValue.angabenZurPerson.derzeitigeStaatsangehoerigkeit1,
          formValue.angabenZurPerson.derzeitigeStaatsangehoerigkeit2,
          formValue.angabenZurPerson.derzeitigeStaatsangehoerigkeit3
        ),
        staatsangehoerigkeitBeiGeburtListe: this.valueListArraySelection(
          'land',
          formValue.angabenZurPerson.staatsangehoerigkeitBeiGeburt1
        ),
        isStellvertreter: formValue.angabenZurPerson.isStellvertreter,
        stellvertreter: {
          familienname: formValue.angabenZurPerson.stellvertreter ? this.get(formValue.angabenZurPerson.stellvertreter.familienname) : null,
          vorname: formValue.angabenZurPerson.stellvertreter ? this.get(formValue.angabenZurPerson.stellvertreter.vorname) : null,
          anschrift: formValue.angabenZurPerson.stellvertreter ? this.get(formValue.angabenZurPerson.stellvertreter.anschrift) : null,
          telefon: formValue.angabenZurPerson.stellvertreter ? this.get(formValue.angabenZurPerson.stellvertreter.telefon) : null,
          email: formValue.angabenZurPerson.stellvertreter ? this.get(formValue.angabenZurPerson.stellvertreter.email) : null
        },
        pass: {
          passnummer: this.get(formValue.ausweispapiere.passNummer),
          passArt: this.valueListSelection('pass_art', formValue.ausweispapiere.passArt),
          ausgestelltVon: this.get(formValue.ausweispapiere.passAusgestelltvon),
          ausstellenderStaat: this.valueListSelection('land', formValue.ausweispapiere.passAusstellerstaat),
          gueltigVon: this.getFormattedDatePickerValue(formValue.ausweispapiere.passAusstellungsdatum),
          gueltigBis: this.getFormattedDatePickerValue(formValue.ausweispapiere.passGueltigbis)
        },

        nationaleIdentNr: this.get(formValue.ausweispapiere.nationaleIdentNr),

        personendaten: {
          abweichendeSchreibweiseName: this.get(formValue.zusaetzlicheAngaben) ? this.get(formValue.zusaetzlicheAngaben.abweichendeSchreibweiseName) : undefined,

          berufdaten: this.get(formValue.angabenZurPerson.isRechtAufFreizuegigkeit) !== true
            ? {
              berufAuswahl: this.valueListSelection('beruf', formValue.angabenZurPerson.beruf.berufAuswahl),
              firmenname: this.get(formValue.angabenZurPerson.beruf.firmenname),
              firmenTelefonNr: this.get(formValue.angabenZurPerson.beruf.firmenTelefonNr),
              strasse: this.get(formValue.angabenZurPerson.beruf.strasse),
              hausnummer: this.get(formValue.angabenZurPerson.beruf.hausnummer),
              sonstigeAdressangaben: this.get(formValue.angabenZurPerson.beruf.sonstigeAdressangaben),
              plz: this.get(formValue.angabenZurPerson.beruf.plz),
              ort: this.get(formValue.angabenZurPerson.beruf.ort),
              land: this.valueListSelection('land', formValue.angabenZurPerson.beruf.land)
            } : undefined,

          staendigeAnschrift: {
            land: this.valueListSelection('land', formValue.kontaktdaten.anschriftLand),
            ort: this.get(formValue.kontaktdaten.anschriftOrt),
            plz: this.get(formValue.kontaktdaten.anschriftPLZ),
            strasse: this.get(formValue.kontaktdaten.anschriftStrasse),
            hausnummer: this.get(formValue.kontaktdaten.anschriftHausnummer),
            sonstigeAdressangaben: this.get(formValue.kontaktdaten.anschriftSonstiges),
            kontaktdaten: {
              email: this.get(formValue.kontaktdaten.anschriftEmail),
              telefon: this.get(formValue.kontaktdaten.anschriftTelefon)
            }
          },
          eltern: {
            vater: {
              staatsangehoerigkeitListe: this.valueListArraySelection(
                'land',
                formValue.angabenZurPerson.staatsangehoerigkeitVater
              ),
              familienname: this.get(formValue.angabenZurPerson.familiennameVater),
              vorname: this.get(formValue.angabenZurPerson.vornameVater)
            },
            mutter: {
              staatsangehoerigkeitListe: this.valueListArraySelection(
                'land',
                formValue.angabenZurPerson.staatsangehoerigkeitMutter
              ),
              familienname: this.get(formValue.angabenZurPerson.familiennameMutter),
              vorname: this.get(formValue.angabenZurPerson.vornameMutter)
            },
            anschrift: this.get(formValue.angabenZurPerson.anschriftEltern),
            kontaktdaten: {
              email: this.get(formValue.angabenZurPerson.emailEltern),
              telefon: this.get(formValue.angabenZurPerson.telefonEltern)
            }
          }
        },

        aufenthaltsberechtigung: formValue.kontaktdaten.wohnsitzAnderesLand === true
          ? {
            artDerRueckkehrberechtigung: this.get(formValue.kontaktdaten.aufenthaltsberechtigungArt),
            rueckkehrDokumentNr: this.get(formValue.kontaktdaten.aufenthaltsberechtigungNummer),
            rueckkehrGueltigBis: this.getFormattedDatePickerValue(formValue.kontaktdaten.aufenthaltsberechtigungGueltigkeitbis)
          }
          : undefined,
        einreisegenehmigung: formValue.reisedaten.einreisegenehmigung
          ? {
            endzielStaat: this.valueListSelection('land', formValue.reisedaten.einreisegenehmigung.endzielStaat),
            gueltigVon: this.get(formValue.reisedaten.einreisegenehmigung.gueltigVon),
            gueltigBis: this.get(formValue.reisedaten.einreisegenehmigung.gueltigBis),
            ausgestelltVon: this.get(formValue.reisedaten.einreisegenehmigung.ausgestelltVon)
          }
          : undefined,

        biometrie: formValue.ausweispapiere.biometrie && (formValue.ausweispapiere.biometrie.fingerabdrueckeErfassungsDatum || formValue.ausweispapiere.biometrie.fingerabdrueckeBereitsErfasst === true)
          ? {
            fingerabdrueckeBereitsErfasst: formValue.ausweispapiere.biometrie.fingerabdrueckeBereitsErfasst,
            fingerabdrueckeErfassungsDatum: this.get(formValue.ausweispapiere.biometrie.fingerabdrueckeErfassungsDatum)
          }
          : null
      },

      reisedaten: {
        aufenthaltszweckListe: this.valueListArraySelection(
          'aufenthaltszweck',
          formValue.reisedaten.reisezweck1,
          formValue.reisedaten.reisezweck2,
          formValue.reisedaten.reisezweck3
        ),
        weitereInformationen: this.get(formValue.reisedaten.weitereInformationen),
        angegebenerReisezweck: this.get(formValue.reisedaten.sonstigerReisezweck),
        ersteinreiseStaat: this.valueListSelection('schengenland', formValue.reisedaten.ersteinreiseStaat),
        hauptzielListe: this.valueListArraySelection(
          'schengenland',
          formValue.reisedaten.zielStaat1,
          formValue.reisedaten.zielStaat2,
          formValue.reisedaten.zielStaat3
        ),


        // wird gesetzt, wenn Recht auf Freizuegigkeit nicht ausgewählt und Referenzart ungleich "keine Referenzperson" ist
        reisekostenUebernahme: this.get(formValue.angabenZurPerson.isRechtAufFreizuegigkeit) !== true && formValue.referenz.art !== '1'
          ? {
            isAntragsteller: this.get(formValue.kostenuebernahme.reisekostenUebernahme.isAntragsteller),
            reisekostenUebernahmeAntragsteller: {
              antragstellerLebensunterhalt: {
                bar: this.get(formValue.kostenuebernahme.reisekostenUebernahme.bar),
                reiseschecks: this.get(formValue.kostenuebernahme.reisekostenUebernahme.reiseschecks),
                kreditkarten: this.get(formValue.kostenuebernahme.reisekostenUebernahme.kreditkarten),
                unterkunft: this.get(formValue.kostenuebernahme.reisekostenUebernahme.unterkunft),
                befoerderung: this.get(formValue.kostenuebernahme.reisekostenUebernahme.befoerderung),
                sonstiges: this.get(formValue.kostenuebernahme.reisekostenUebernahme.sonstigesText)
              }
            },
            isDritte: this.get(formValue.kostenuebernahme.reisekostenUebernahme.isDritte),
            dritte: {
              organisation: this.get(formValue.kostenuebernahme.reisekostenUebernahme.organisation),
              einlader: this.get(formValue.kostenuebernahme.reisekostenUebernahme.einlader),
              dritteLebensunterhalt: {
                bar: this.get(formValue.kostenuebernahme.reisekostenUebernahme.dritteBar),
                unterkunft: this.get(formValue.kostenuebernahme.reisekostenUebernahme.dritteUnterkunft),
                vollstaendigeKostenuebernahme: this.get(formValue.kostenuebernahme.reisekostenUebernahme.dritteVollstaendigeKostenuebernahme),
                befoerderung: this.get(formValue.kostenuebernahme.reisekostenUebernahme.dritteBefoerderung),
                sonstiges: this.get(formValue.kostenuebernahme.reisekostenUebernahme.dritteSonstigesText)
              }
            }
          } : undefined,

        befoerderungsmittel: this.get(formValue.zusaetzlicheAngaben) ? this.valueListSelection('befoerderungsmittel', formValue.zusaetzlicheAngaben.befoerderungsmittel) : undefined,
        letzteVisumStickernummer: this.marshallStickernummer(formValue),
        reisekrankenversicherung: this.get(formValue.zusaetzlicheAngaben) ? {
          daten: this.get(formValue.zusaetzlicheAngaben.rkvJaNein) === true ? { gesellschaft: this.get(formValue.zusaetzlicheAngaben.rkvAnbieter) } : undefined
        } : undefined
      },

      // Get the reference
      referenz: this.marshallReferenz(formValue),

      // ok
      visumdaten: {
        gueltigkeit: {
          von: this.getFormattedDatePickerValue(formValue.reisedaten.ankunftsdatum),
          bisGenau: {
            value: this.getFormattedDatePickerValue(formValue.reisedaten.abreisedatum)
          }
        },
        anzahlEinreisen: this.valueListSelection('anzahleinreisen', formValue.reisedaten.anzahlEinreisen)
      },

      sonstiges: this.get(formValue.zusaetzlicheAngaben) ? {
        abholer: this.get(formValue.zusaetzlicheAngaben.abholer),
        familienname: this.get(formValue.zusaetzlicheAngaben.abholer) === true ? this.get(formValue.zusaetzlicheAngaben.familienname) : undefined,
        vorname: this.get(formValue.zusaetzlicheAngaben.abholer) === true ? this.get(formValue.zusaetzlicheAngaben.vorname) : undefined,
        anschrift: this.get(formValue.zusaetzlicheAngaben.abholer) === true ? this.get(formValue.zusaetzlicheAngaben.anschrift) : undefined
      } : undefined,

      zusatzangaben: this.get(formValue.zusaetzlicheAngaben) ? {
        weitereAngaben: this.get(formValue.zusaetzlicheAngaben.weitereAngaben)
      } : undefined,

      // wird gesetzt, wenn Recht auf Freizuegigkeit nicht ausgewählt wurde und referenz.art ungleich "keine Referenz" ist
      verpflichtungserklaerungsgeber: this.isNotRechtAufFreizuegigkeitAndNoAbweichenderVerpflichtungsgeber(formValue)
        ? {
          art: this.valueListSelection('verpflichtungserklaerungsgeber_art', formValue.kostenuebernahme.verpflichtungserklaerungsgeber.art),

          // 2 = organisation, 3 = diplomat
          organisation: (formValue.kostenuebernahme.verpflichtungserklaerungsgeber.art === '2')
            ? {
              name: this.get(formValue.kostenuebernahme.verpflichtungserklaerungsgeber.orgaName),
              land: this.valueListSelection('land', formValue.kostenuebernahme.verpflichtungserklaerungsgeber.orgaSitzLand),
              sitz: this.get(formValue.kostenuebernahme.verpflichtungserklaerungsgeber.orgaSitz),
              wirkungsbereich: this.valueListSelection('organisation_wirkungsbereich', formValue.kostenuebernahme.verpflichtungserklaerungsgeber.orgaAufgabe),
              registerName: this.valueListSelection('organisation_register', formValue.kostenuebernahme.verpflichtungserklaerungsgeber.orgaRegisterName),
              registerOrt: this.get(formValue.kostenuebernahme.verpflichtungserklaerungsgeber.orgaRegisterOrt),
              registerNummer: this.get(formValue.kostenuebernahme.verpflichtungserklaerungsgeber.orgaRegisternummer)
            }
            : (formValue.kostenuebernahme.verpflichtungserklaerungsgeber.art === '3')
              ? { name: this.get(formValue.kostenuebernahme.verpflichtungserklaerungsgeber.orgaName) }
              : undefined,

          ansprechpartner: {
            staatsangehoerigkeit: this.valueListSelection('land', formValue.kostenuebernahme.verpflichtungserklaerungsgeber.staatsangehoerigkeit),
            familienname: this.get(formValue.kostenuebernahme.verpflichtungserklaerungsgeber.familienname),
            vorname: this.get(formValue.kostenuebernahme.verpflichtungserklaerungsgeber.vorname),
            geburtsort: this.get(formValue.kostenuebernahme.verpflichtungserklaerungsgeber.geburtsort),
            geburtsdatum: this.get(formValue.kostenuebernahme.verpflichtungserklaerungsgeber.geburtsdatum),
            geschlecht: this.valueListSelection('geschlecht', formValue.kostenuebernahme.verpflichtungserklaerungsgeber.geschlecht),
            anschrift: {
              land: this.valueListSelection('land', formValue.kostenuebernahme.verpflichtungserklaerungsgeber.land),
              ort: this.get(formValue.kostenuebernahme.verpflichtungserklaerungsgeber.ort),
              plz: this.get(formValue.kostenuebernahme.verpflichtungserklaerungsgeber.plz),
              strasse: this.get(formValue.kostenuebernahme.verpflichtungserklaerungsgeber.strasse),
              hausnummer: this.get(formValue.kostenuebernahme.verpflichtungserklaerungsgeber.hausnummer)
            },
            kontaktdaten: {
              email: this.get(formValue.kostenuebernahme.verpflichtungserklaerungsgeber.email),
              telefon: this.get(formValue.kostenuebernahme.verpflichtungserklaerungsgeber.telefon)
            },

            // abweichende Schreibweisen und frühere/andere Namen nur bei abweichenderVerpflichtungsgeber.art "Person"
            abweichendeSchreibweiseNachname: formValue.kostenuebernahme.verpflichtungserklaerungsgeber.art === '1'
              ? this.get(formValue.kostenuebernahme.verpflichtungserklaerungsgeber.abweichendNachname)
              : undefined,
            abweichendeSchreibweiseVorname: formValue.kostenuebernahme.verpflichtungserklaerungsgeber.art === '1'
              ? this.get(formValue.kostenuebernahme.verpflichtungserklaerungsgeber.abweichendVorname)
              : undefined,
            fruehereNamen: formValue.kostenuebernahme.verpflichtungserklaerungsgeber.art === '1'
              ? this.get(formValue.kostenuebernahme.verpflichtungserklaerungsgeber.fruehereNamen)
              : undefined,
            andereNamen: formValue.kostenuebernahme.verpflichtungserklaerungsgeber.art === '1'
              ? this.get(formValue.kostenuebernahme.verpflichtungserklaerungsgeber.andereNamen)
              : undefined
          },

          foermlicheVerpflichtungserklaerung: this.get(formValue.zusaetzlicheAngaben)
          && this.getFormattedDatePickerValue(formValue.zusaetzlicheAngaben.foermlicheVerpflichtungserklaerungDatum)
            ? { datum: this.getFormattedDatePickerValue(formValue.zusaetzlicheAngaben.foermlicheVerpflichtungserklaerungDatum) }
            : undefined
        }
        : undefined,

      rechtAufFreizuegigkeit: !!formValue.angabenZurPerson.isRechtAufFreizuegigkeit
    };

    return ret;
  }

  private marshallStickernummer(formValue: any): any {

    if (this.isStickernummerInZusaetzlicheAngaben(formValue)) {
      return {
        schengenLaendercode: this.valueListSelection(
          'schengenlaendercode',
          formValue.zusaetzlicheAngaben.stickernummer.letzteVisumStickernummerSchengenLaendercode
        ),
        nummer: this.get(formValue.zusaetzlicheAngaben.stickernummer.letzteVisumStickernummerNummer)
      };
    } else if (this.isStickernummerInBiometrie(formValue)) {
      return {
        schengenLaendercode: this.valueListSelection(
          'schengenlaendercode',
          formValue.ausweispapiere.biometrie.stickernummer.letzteVisumStickernummerSchengenLaendercode
        ),
        nummer: this.get(formValue.ausweispapiere.biometrie.stickernummer.letzteVisumStickernummerNummer)
      };
    }
    return null;
  }

  private isStickernummerInZusaetzlicheAngaben(formValue: any): boolean {
    return this.appConfig.isZusaetzlicheAngabenActive === true
      && this.get(formValue.zusaetzlicheAngaben)
      && this.get(formValue.zusaetzlicheAngaben.stickernummer)
      && this.get(formValue.zusaetzlicheAngaben.stickernummer.letzteVisumStickernummer) === true;
  }

  private isStickernummerInBiometrie(formValue: any): boolean {
    return this.appConfig.isZusaetzlicheAngabenActive !== true
      && this.get(formValue.ausweispapiere)
      && this.get(formValue.ausweispapiere.biometrie)
      && this.get(formValue.ausweispapiere.biometrie.stickernummer)
      && this.get(formValue.ausweispapiere.biometrie.stickernummer.letzteVisumStickernummer) === true;
  }

  public unmarshall(backendObject: any): any {

    if (this.getAntragsart(backendObject) !== this.appConfig.visumAntragsartEnum) {
      return {};
    }

    return {
      angabenZurPerson: {
        isRechtAufFreizuegigkeit: _get(backendObject, 'rechtAufFreizuegigkeit', null),
        familienname: _get(backendObject, 'antragsteller.familienname', null),
        vorname: _get(backendObject, 'antragsteller.vorname', null),
        geburtsdatum: _get(backendObject, 'antragsteller.geburtsdatum', null),
        geburtsname: _get(backendObject, 'antragsteller.geburtsname', null),
        geburtsort: _get(backendObject, 'antragsteller.geburtsort', null),
        geburtsland: _get(backendObject, 'antragsteller.geburtsland.valueListItemKey', null),
        familienstand: _get(backendObject, 'antragsteller.familienstand.valueListItemKey', null),
        geschlecht: _get(backendObject, 'antragsteller.geschlecht.valueListItemKey', null),
        derzeitigeStaatsangehoerigkeit1: _get(backendObject, 'antragsteller.staatsangehoerigkeitListe[0].valueListItemKey', null),
        derzeitigeStaatsangehoerigkeit2: _get(backendObject, 'antragsteller.staatsangehoerigkeitListe[1].valueListItemKey', null),
        derzeitigeStaatsangehoerigkeit3: _get(backendObject, 'antragsteller.staatsangehoerigkeitListe[2].valueListItemKey', null),
        staatsangehoerigkeitBeiGeburt1: _get(backendObject, 'antragsteller.staatsangehoerigkeitBeiGeburtListe[0].valueListItemKey', null),

        isStellvertreter:  _get(backendObject, "antragsteller.isStellvertreter", false),
        stellvertreter: {
          familienname: _get(backendObject, "antragsteller.stellvertreter.familienname", null),
          vorname: _get(backendObject, "antragsteller.stellvertreter.vorname", null),
          anschrift: _get(backendObject, "antragsteller.stellvertreter.anschrift", null),
          telefon: _get(backendObject, "antragsteller.stellvertreter.telefon", null),
          email: _get(backendObject, "antragsteller.stellvertreter.email", null)
        },

        familiennameVater: _get(backendObject, 'antragsteller.personendaten.eltern.vater.familienname', null),
        vornameVater: _get(backendObject, 'antragsteller.personendaten.eltern.vater.vorname', null),
        staatsangehoerigkeitVater: _get(backendObject, 'antragsteller.personendaten.eltern.vater.staatsangehoerigkeitListe[0].valueListItemKey', null),

        familiennameMutter: _get(backendObject, 'antragsteller.personendaten.eltern.mutter.familienname', null),
        vornameMutter: _get(backendObject, 'antragsteller.personendaten.eltern.mutter.vorname', null),
        staatsangehoerigkeitMutter: _get(backendObject, 'antragsteller.personendaten.eltern.mutter.staatsangehoerigkeitListe[0].valueListItemKey', null),

        anschriftEltern: _get(backendObject, 'antragsteller.personendaten.eltern.anschrift', null),
        telefonEltern: _get(backendObject, 'antragsteller.personendaten.eltern.kontaktdaten.telefon', null),
        emailEltern: _get(backendObject, 'antragsteller.personendaten.eltern.kontaktdaten.email', null),

        beruf: {
          berufAuswahl: _get(backendObject, 'antragsteller.personendaten.berufdaten.berufAuswahl.valueListItemKey', null),
          firmenname: _get(backendObject, 'antragsteller.personendaten.berufdaten.firmenname', null),
          firmenTelefonNr: _get(backendObject, 'antragsteller.personendaten.berufdaten.firmenTelefonNr', null),
          strasse: _get(backendObject, 'antragsteller.personendaten.berufdaten.strasse', null),
          hausnummer: _get(backendObject, 'antragsteller.personendaten.berufdaten.hausnummer', null),
          sonstigeAdressangaben: _get(backendObject, 'antragsteller.personendaten.berufdaten.sonstigeAdressangaben', null),
          plz: _get(backendObject, 'antragsteller.personendaten.berufdaten.plz', null),
          ort: _get(backendObject, 'antragsteller.personendaten.berufdaten.ort', null),
          land: _get(backendObject, 'antragsteller.personendaten.berufdaten.land.valueListItemKey', null)
        },
        verwandter: _get(backendObject, 'rechtAufFreizuegigkeit', null) === true ? {
          verwandschaftsbeziehung: _get(backendObject, 'referenz.verwandtschaftsbeziehungBeiEUFreizuegigkeit.value.valueListItemKey', null),
          familienname: _get(backendObject, 'referenz.ansprechpartner.familienname', null),
          vorname: _get(backendObject, 'referenz.ansprechpartner.vorname', null),
          geburtsdatum: _get(backendObject, 'referenz.ansprechpartner.geburtsdatum', null),
          staatsangehoerigkeit: _get(backendObject, 'referenz.ansprechpartner.staatsangehoerigkeit.valueListItemKey', null),
          passnummer: _get(backendObject, 'referenz.passnummer', null)
        } : null
      },
      kontaktdaten: {
        anschriftStrasse: _get(backendObject, 'antragsteller.personendaten.staendigeAnschrift.strasse', null),
        anschriftHausnummer: _get(backendObject, 'antragsteller.personendaten.staendigeAnschrift.hausnummer', null),
        anschriftSonstiges: _get(backendObject, 'antragsteller.personendaten.staendigeAnschrift.sonstigeAdressangaben', null),
        anschriftPLZ: _get(backendObject, 'antragsteller.personendaten.staendigeAnschrift.plz', null),
        anschriftOrt: _get(backendObject, 'antragsteller.personendaten.staendigeAnschrift.ort', null),
        anschriftLand: _get(backendObject, 'antragsteller.personendaten.staendigeAnschrift.land.valueListItemKey', null),
        anschriftTelefon: _get(backendObject, 'antragsteller.personendaten.staendigeAnschrift.kontaktdaten.telefon', null),
        anschriftEmail: _get(backendObject, 'antragsteller.personendaten.staendigeAnschrift.kontaktdaten.email', null),

        aufenthaltsberechtigungArt: _get(backendObject, 'antragsteller.aufenthaltsberechtigung.artDerRueckkehrberechtigung', null),
        aufenthaltsberechtigungNummer: _get(backendObject, 'antragsteller.aufenthaltsberechtigung.rueckkehrDokumentNr', null),
        aufenthaltsberechtigungGueltigkeitbis: this.stringAsDatepickerModel(
          _get(backendObject, 'antragsteller.aufenthaltsberechtigung.rueckkehrGueltigBis', null)
        ),
        wohnsitzAnderesLand: _get(backendObject, 'antragsteller.aufenthaltsberechtigung', null) != null,
        wohnsitzAnderesLandAuswahl:
          (_get(backendObject, 'antragsteller.aufenthaltsberechtigung', null) != null) ? 'YES' : 'NO'
      },
      ausweispapiere: {
        passArt: _get(backendObject, 'antragsteller.pass.passArt.valueListItemKey', null),
        passNummer: _get(backendObject, 'antragsteller.pass.passnummer', null),
        passAusstellungsdatum: this.stringAsDatepickerModel(_get(backendObject, 'antragsteller.pass.gueltigVon', null)),
        passGueltigbis: this.stringAsDatepickerModel(_get(backendObject, 'antragsteller.pass.gueltigBis', null)),
        passAusstellerstaat: _get(backendObject, 'antragsteller.pass.ausstellenderStaat.valueListItemKey', null),
        passAusgestelltvon: _get(backendObject, 'antragsteller.pass.ausgestelltVon', null),
        nationaleIdentNr: _get(backendObject, 'antragsteller.nationaleIdentNr', null),
        biometrie: this.unmarshallBiometrie(backendObject)
      },
      reisedaten: {
        reisezweck1: _get(backendObject, 'reisedaten.aufenthaltszweckListe[0].valueListItemKey', null),
        reisezweck2: _get(backendObject, 'reisedaten.aufenthaltszweckListe[1].valueListItemKey', null),
        reisezweck3: _get(backendObject, 'reisedaten.aufenthaltszweckListe[2].valueListItemKey', null),
        weitereInformationen: _get(backendObject, 'reisedaten.weitereInformationen', null),
        sonstigerReisezweck: _get(backendObject, 'reisedaten.angegebenerReisezweck', null),
        ersteinreiseStaat: _get(backendObject, 'reisedaten.ersteinreiseStaat.valueListItemKey', null),

        zielStaat1: _get(backendObject, 'reisedaten.hauptzielListe[0].valueListItemKey', null),
        zielStaat2: _get(backendObject, 'reisedaten.hauptzielListe[1].valueListItemKey', null),
        zielStaat3: _get(backendObject, 'reisedaten.hauptzielListe[2].valueListItemKey', null),
        anzahlEinreisen: _get(backendObject, 'visumdaten.anzahlEinreisen.valueListItemKey', null),

        ankunftsdatum: this.stringAsDatepickerModel(_get(backendObject, 'visumdaten.gueltigkeit.von', null)),
        abreisedatum: this.stringAsDatepickerModel(_get(backendObject, 'visumdaten.gueltigkeit.bisGenau.value', null)),

        einreisegenehmigung: {
          endzielStaat: _get(backendObject, 'antragsteller.einreisegenehmigung.endzielStaat.valueListItemKey', null),
          gueltigVon: _get(backendObject, 'antragsteller.einreisegenehmigung.gueltigVon', null),
          gueltigBis: _get(backendObject, 'antragsteller.einreisegenehmigung.gueltigBis', null),
          ausgestelltVon: _get(backendObject, 'antragsteller.einreisegenehmigung.ausgestelltVon', null)
        }
      },
      referenz: _get(backendObject, 'rechtAufFreizuegigkeit', null) !== true ? this.unmarshallReferenz(backendObject) : null,
      kostenuebernahme: {
        reisekostenUebernahme: {
          antragsteller: _get(backendObject, 'reisedaten.reisekostenUebernahme.antragsteller', null),
          organisation: _get(backendObject, 'reisedaten.reisekostenUebernahme.dritte.organisation', null),
          einlader: _get(backendObject, 'reisedaten.reisekostenUebernahme.dritte.einlader', null),

          isAntragsteller: _get(backendObject, 'reisedaten.reisekostenUebernahme.isAntragsteller', null),
          sonstigesText: _get(backendObject, 'reisedaten.reisekostenUebernahme.reisekostenUebernahmeAntragsteller.antragstellerLebensunterhalt.sonstiges', null),
          bar: _get(backendObject, 'reisedaten.reisekostenUebernahme.reisekostenUebernahmeAntragsteller.antragstellerLebensunterhalt.bar', null),
          reiseschecks: _get(backendObject, 'reisedaten.reisekostenUebernahme.reisekostenUebernahmeAntragsteller.antragstellerLebensunterhalt.reiseschecks', null),
          kreditkarten: _get(backendObject, 'reisedaten.reisekostenUebernahme.reisekostenUebernahmeAntragsteller.antragstellerLebensunterhalt.kreditkarten', null),
          unterkunft: _get(backendObject, 'reisedaten.reisekostenUebernahme.reisekostenUebernahmeAntragsteller.antragstellerLebensunterhalt.unterkunft', null),
          befoerderung: _get(backendObject, 'reisedaten.reisekostenUebernahme.reisekostenUebernahmeAntragsteller.antragstellerLebensunterhalt.befoerderung', null),
          sonstiges: !!_get(backendObject, 'reisedaten.reisekostenUebernahme.reisekostenUebernahmeAntragsteller.antragstellerLebensunterhalt.sonstiges', null),

          isDritte: _get(backendObject, 'reisedaten.reisekostenUebernahme.isDritte', null),
          dritteOrganisation: _get(backendObject, 'reisedaten.reisekostenUebernahme.dritte.organisation', null),
          dritteEinlader: _get(backendObject, 'reisedaten.reisekostenUebernahme.dritte.einlader', null),
          dritteSonstigesText: _get(backendObject, 'reisedaten.reisekostenUebernahme.dritte.dritteLebensunterhalt.sonstiges', null),
          dritteBar: _get(backendObject, 'reisedaten.reisekostenUebernahme.dritte.dritteLebensunterhalt.bar', null),
          dritteUnterkunft: _get(backendObject, 'reisedaten.reisekostenUebernahme.dritte.dritteLebensunterhalt.unterkunft', null),
          dritteVollstaendigeKostenuebernahme: _get(backendObject, 'reisedaten.reisekostenUebernahme.dritte.dritteLebensunterhalt.vollstaendigeKostenuebernahme', null),
          dritteBefoerderung: _get(backendObject, 'reisedaten.reisekostenUebernahme.dritte.dritteLebensunterhalt.befoerderung', null),
          dritteSonstiges: !!_get(backendObject, 'reisedaten.reisekostenUebernahme.dritte.dritteLebensunterhalt.sonstiges', null)
        },
        verpflichtungserklaerungsgeber: {
          art: _get(backendObject, 'verpflichtungserklaerungsgeber.art.valueListItemKey', null),
          familienname: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.familienname', null),
          vorname: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.vorname', null),
          geschlecht: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.geschlecht.valueListItemKey', null),
          geburtsdatum: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.geburtsdatum', null),
          geburtsort: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.geburtsort', null),
          staatsangehoerigkeit: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.staatsangehoerigkeit.valueListItemKey', null),
          strasse: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.anschrift.strasse', null),
          hausnummer: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.anschrift.hausnummer', null),
          plz: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.anschrift.plz', null),
          ort: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.anschrift.ort', null),
          land: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.anschrift.land.valueListItemKey', null),
          telefon: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.kontaktdaten.telefon', null),
          email: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.kontaktdaten.email', null),
          abweichendNachname: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.abweichendeSchreibweiseNachname', null),
          abweichendVorname: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.abweichendeSchreibweiseVorname', null),
          andereNamen: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.andereNamen', null),
          fruehereNamen: _get(backendObject, 'verpflichtungserklaerungsgeber.ansprechpartner.fruehereNamen', null),
          orgaName: _get(backendObject, 'verpflichtungserklaerungsgeber.organisation.name', null),
          orgaSitz: _get(backendObject, 'verpflichtungserklaerungsgeber.organisation.sitz', null),
          orgaSitzLand: _get(backendObject, 'verpflichtungserklaerungsgeber.organisation.land.valueListItemKey', null),
          orgaAufgabe: _get(backendObject, 'verpflichtungserklaerungsgeber.organisation.wirkungsbereich.valueListItemKey', null),
          orgaRegisterName: _get(backendObject, 'verpflichtungserklaerungsgeber.organisation.registerName.valueListItemKey', null),
          orgaRegisterOrt: _get(backendObject, 'verpflichtungserklaerungsgeber.organisation.registerOrt', null),
          orgaRegisternummer: _get(backendObject, 'verpflichtungserklaerungsgeber.organisation.registerNummer', null)
        }
      },
      zusaetzlicheAngaben: this.appConfig.isZusaetzlicheAngabenActive === true ? {
        abweichendeSchreibweiseName: _get(backendObject, 'antragsteller.personendaten.abweichendeSchreibweiseName', null),
        befoerderungsmittel: _get(backendObject, 'reisedaten.befoerderungsmittel.valueListItemKey', null),
        rkvJaNein: _get(backendObject, 'reisedaten.reisekrankenversicherung.daten', null) !== null,
        rkvAnbieter: _get(backendObject, 'reisedaten.reisekrankenversicherung.daten.gesellschaft', null),
        abholer: _get(backendObject, 'sonstiges.abholer', null),
        familienname: _get(backendObject, 'sonstiges.familienname', null),
        vorname: _get(backendObject, 'sonstiges.vorname', null),
        anschrift: _get(backendObject, 'sonstiges.anschrift', null),
        weitereAngaben: _get(backendObject, 'zusatzangaben.weitereAngaben', null),
        begruedungFuerFehlendeReferenz: _get(backendObject, 'referenz.begruedungFuerFehlendeReferenz.valueListItemKey'),
        foermlicheVerpflichtungserklaerungDatum: this.stringAsDatepickerModel(_get(backendObject, 'referenz.foermlicheVerpflichtungserklaerung.datum')),
        stickernummer: this.unmarshallStickernummer(backendObject)
      } : null,

      fingerabdruck: {},
      lichtbild: {},
      dokumente: {},

      lebensunterhaltAufenthaltsdetails: {}
    };
  }

  private toBool(val: any): boolean {
    if (val === true) { return true; }
    if (val === false || val == null) { return false; }

    const s = String(val).trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes' || s === 'ja' || s === 'j';
  }

  private unmarshallBiometrie(backendObject: any): any {
    const biometrieObj = _get(backendObject, 'antragsteller.biometrie', null);
    if (biometrieObj == null) {
      const stickernummerPresentForBiometrie =
        this.appConfig.isZusaetzlicheAngabenActive !== true && this.isStickernummerPresent(backendObject);

      return {
        fingerabdrueckeBereitsErfasst: false,
        fingerabdrueckeBereitsErfasstAuswahl: 'NO',
        fingerabdrueckeErfassungsDatum: null,
        stickernummer: stickernummerPresentForBiometrie ? this.unmarshallStickernummer(backendObject) : null
      };
    }

    const raw = _get(backendObject, 'antragsteller.biometrie.fingerabdrueckeBereitsErfasst', null);
    const boolVal = this.toBool(raw);
    const auswahl: 'YES' | 'NO' = boolVal ? 'YES' : 'NO';

    const stickernummerPresentForBiometrie =
      this.appConfig.isZusaetzlicheAngabenActive !== true && this.isStickernummerPresent(backendObject);

    return {
      fingerabdrueckeBereitsErfasst: boolVal,
      fingerabdrueckeBereitsErfasstAuswahl: auswahl,
      fingerabdrueckeErfassungsDatum: _get(backendObject, 'antragsteller.biometrie.fingerabdrueckeErfassungsDatum', null),
      stickernummer: stickernummerPresentForBiometrie ? this.unmarshallStickernummer(backendObject) : null
    };
  }

  private isStickernummerPresent(backendObject: any): boolean {
    return _get(backendObject, 'reisedaten.letzteVisumStickernummer', null) != null;
  }

  private unmarshallStickernummer(backendObject: any): any {
    return this.isStickernummerPresent(backendObject) ? {
        letzteVisumStickernummer: true,
        letzteVisumStickernummerSchengenLaendercode: _get(backendObject, 'reisedaten.letzteVisumStickernummer.schengenLaendercode.valueListItemKey', null),
        letzteVisumStickernummerNummer: _get(backendObject, 'reisedaten.letzteVisumStickernummer.nummer', null)
      }
      : null;
  }

  marshallKeineReferenzperson(formValue: any) {
    return {
      referenzArt: this.getReferenzArt(formValue)
    };
  }

  marshallReferenzperson(formValue: any) {
    return {
      referenzArt: this.getReferenzArt(formValue),
      begruedungFuerFehlendeReferenz: this.getBegruendungFuerFehlendeReferenz(formValue),
      foermlicheVerpflichtungserklaerung: this.getFoermlicheVerpflichtungserklaerung(formValue),
      passnummer: this.getPassnummer(formValue),
      verwandtschaftsbeziehungBeiEUFreizuegigkeit: this.getVerwandschaftsbeziehungBeiEuFreizuegigkeit(formValue),
      abweichenderVerpflichtungsgeber: this.getAbweichenderVerpflichtungsgeber(formValue),

      verwandtschaftsbeziehungFreitext: this.get(formValue.referenz.einladendePerson_referenzPerson.verwandtschaftsverhaeltnis),

      ansprechpartner: this.isNotRechtAufFreizuegigkeitAndNotKeineReferenzPerson(formValue)
        ? this.getAnsprechpartnerFuerReferenzReferenzperson(formValue)
        : this.marshallVerwandter(formValue)
    };
  }

  marshallOrganisation(formValue: any) {
    return {
      referenzArt: this.getReferenzArt(formValue),
      begruedungFuerFehlendeReferenz: this.getBegruendungFuerFehlendeReferenz(formValue),
      foermlicheVerpflichtungserklaerung: this.getFoermlicheVerpflichtungserklaerung(formValue),
      passnummer: this.getPassnummer(formValue),
      verwandtschaftsbeziehungBeiEUFreizuegigkeit: this.getVerwandschaftsbeziehungBeiEuFreizuegigkeit(formValue),
      abweichenderVerpflichtungsgeber: this.getAbweichenderVerpflichtungsgeber(formValue),

      organisation: this.getOrganisationFuerReferenzOrganisation(formValue),

      ansprechpartner: this.isNotRechtAufFreizuegigkeitAndNotKeineReferenzPerson(formValue)
        ? this.getAnsprechpartnerFuerReferenzOrganisation(formValue)
        : this.marshallVerwandter(formValue)
    };
  }

  marshallHotel(formValue: any) {
    return {
      referenzArt: this.getReferenzArt(formValue),
      begruedungFuerFehlendeReferenz: this.getBegruendungFuerFehlendeReferenz(formValue),
      foermlicheVerpflichtungserklaerung: this.getFoermlicheVerpflichtungserklaerung(formValue),
      passnummer: this.getPassnummer(formValue),
      verwandtschaftsbeziehungBeiEUFreizuegigkeit: this.getVerwandschaftsbeziehungBeiEuFreizuegigkeit(formValue),
      abweichenderVerpflichtungsgeber: this.getAbweichenderVerpflichtungsgeber(formValue),

      organisation: {
        name: this.get(formValue.referenz.hotel.orgaName),
        reservierungsnummer: this.get(formValue.referenz.hotel.reservierungsnummer),
        sitz: this.get(formValue.referenz.hotel.orgaSitz),
        land: this.valueListSelection('land', formValue.referenz.hotel.orgaSitzLand),
        wirkungsbereich: this.valueListSelection('organisation_wirkungsbereich', formValue.referenz.hotel.orgaAufgabe),
        registerName: this.valueListSelection('organisation_register', formValue.referenz.hotel.orgaRegisterName),
        registerOrt: this.get(formValue.referenz.hotel.orgaRegisterOrt),
        registerNummer: this.get(formValue.referenz.hotel.orgaRegisternummer)
      },
      ansprechpartner: this.isNotRechtAufFreizuegigkeitAndNotKeineReferenzPerson(formValue) ? {
        familienname: this.get(formValue.referenz.hotel.familienname),
        vorname: this.get(formValue.referenz.hotel.vorname),
        anschrift: {
          strasse: this.get(formValue.referenz.hotel.strasse),
          hausnummer: this.get(formValue.referenz.hotel.hausnummer),
          plz: this.get(formValue.referenz.hotel.plz),
          ort: this.get(formValue.referenz.hotel.ort),
          land: this.valueListSelection('land', formValue.referenz.hotel.land)
        },
        kontaktdaten: {
          email: this.get(formValue.referenz.hotel.email),
          telefon: this.get(formValue.referenz.hotel.telefon)
        }
      } : this.marshallVerwandter(formValue)
    };
  }

  marshallAkkreditierterDiplomat(formValue: any) {
    return {
      referenzArt: this.getReferenzArt(formValue),
      begruedungFuerFehlendeReferenz: this.getBegruendungFuerFehlendeReferenz(formValue),
      foermlicheVerpflichtungserklaerung: this.getFoermlicheVerpflichtungserklaerung(formValue),
      passnummer: this.getPassnummer(formValue),
      verwandtschaftsbeziehungBeiEUFreizuegigkeit: this.getVerwandschaftsbeziehungBeiEuFreizuegigkeit(formValue),
      abweichenderVerpflichtungsgeber: this.getAbweichenderVerpflichtungsgeber(formValue),

      organisation: {
        name: this.get(formValue.referenz.akkreditierterDiplomat.orgaName)
      },

      ansprechpartner: this.isNotRechtAufFreizuegigkeitAndNotKeineReferenzPerson(formValue)
        ? this.getAnsprechpartnerFuerReferenzAkkreditierterDiplomat(formValue)
        : this.marshallVerwandter(formValue)
    };
  }

  marshallEuBuerger(formValue: any) {
    return {
      referenzArt: this.getReferenzArt(formValue),
      begruedungFuerFehlendeReferenz: this.getBegruendungFuerFehlendeReferenz(formValue),
      foermlicheVerpflichtungserklaerung: this.getFoermlicheVerpflichtungserklaerung(formValue),
      passnummer: this.getPassnummer(formValue),
      verwandtschaftsbeziehungBeiEUFreizuegigkeit: this.getVerwandschaftsbeziehungBeiEuFreizuegigkeit(formValue),
      abweichenderVerpflichtungsgeber: this.getAbweichenderVerpflichtungsgeber(formValue),

      ansprechpartner: this.isNotRechtAufFreizuegigkeitAndNotKeineReferenzPerson(formValue) ? {
        familienname: this.get(formValue.referenz.euBuerger.familienname),
        vorname: this.get(formValue.referenz.euBuerger.vorname),
        geschlecht: this.valueListSelection('geschlecht', formValue.referenz.euBuerger.geschlecht),
        geburtsdatum: this.get(formValue.referenz.euBuerger.geburtsdatum),
        geburtsort: this.get(formValue.referenz.euBuerger.geburtsort),
        staatsangehoerigkeit: this.valueListSelection('land', formValue.referenz.euBuerger.staatsangehoerigkeit),
        anschrift: {
          strasse: this.get(formValue.referenz.euBuerger.strasse),
          hausnummer: this.get(formValue.referenz.euBuerger.hausnummer),
          plz: this.get(formValue.referenz.euBuerger.plz),
          ort: this.get(formValue.referenz.euBuerger.ort),
          land: this.valueListSelection('land', formValue.referenz.euBuerger.land)
        },
        kontaktdaten: {
          email: this.get(formValue.referenz.euBuerger.email),
          telefon: this.get(formValue.referenz.euBuerger.telefon)
        },
        abweichendeSchreibweiseNachname: this.get(formValue.referenz.euBuerger.abweichendNachname),
        abweichendeSchreibweiseVorname: this.get(formValue.referenz.euBuerger.abweichendVorname),
        andereNamen: this.get(formValue.referenz.euBuerger.andereNamen),
        fruehereNamen: this.get(formValue.referenz.euBuerger.fruehereNamen)
      } : this.marshallVerwandter(formValue)
    };
  }

  marshallWohungsinhaber(formValue: any) {
    return {
      referenzArt: this.getReferenzArt(formValue),
      begruedungFuerFehlendeReferenz: this.getBegruendungFuerFehlendeReferenz(formValue),
      foermlicheVerpflichtungserklaerung: this.getFoermlicheVerpflichtungserklaerung(formValue),
      passnummer: this.getPassnummer(formValue),
      verwandtschaftsbeziehungBeiEUFreizuegigkeit: this.getVerwandschaftsbeziehungBeiEuFreizuegigkeit(formValue),
      abweichenderVerpflichtungsgeber: this.getAbweichenderVerpflichtungsgeber(formValue),

      ansprechpartner: this.isNotRechtAufFreizuegigkeitAndNotKeineReferenzPerson(formValue) ? {
        familienname: this.get(formValue.referenz.wohnungsInhaber.familienname),
        vorname: this.get(formValue.referenz.wohnungsInhaber.vorname),
        geschlecht: this.valueListSelection('geschlecht', formValue.referenz.wohnungsInhaber.geschlecht),
        geburtsdatum: this.get(formValue.referenz.wohnungsInhaber.geburtsdatum),
        geburtsort: this.get(formValue.referenz.wohnungsInhaber.geburtsort),
        staatsangehoerigkeit: this.valueListSelection('land', formValue.referenz.wohnungsInhaber.staatsangehoerigkeit),
        anschrift: {
          strasse: this.get(formValue.referenz.wohnungsInhaber.strasse),
          hausnummer: this.get(formValue.referenz.wohnungsInhaber.hausnummer),
          plz: this.get(formValue.referenz.wohnungsInhaber.plz),
          ort: this.get(formValue.referenz.wohnungsInhaber.ort),
          land: this.valueListSelection('land', formValue.referenz.wohnungsInhaber.land)
        },
        kontaktdaten: {
          email: this.get(formValue.referenz.wohnungsInhaber.email),
          telefon: this.get(formValue.referenz.wohnungsInhaber.telefon)
        },
        abweichendeSchreibweiseNachname: this.get(formValue.referenz.wohnungsInhaber.abweichendNachname),
        abweichendeSchreibweiseVorname: this.get(formValue.referenz.wohnungsInhaber.abweichendVorname),
        andereNamen: this.get(formValue.referenz.wohnungsInhaber.andereNamen),
        fruehereNamen: this.get(formValue.referenz.wohnungsInhaber.fruehereNamen)
      } : this.marshallVerwandter(formValue)
    };
  }

  marshallGewerblicheAnmietung(formValue: any) {
    return {
      referenzArt: this.getReferenzArt(formValue),
      begruedungFuerFehlendeReferenz: this.getBegruendungFuerFehlendeReferenz(formValue),
      foermlicheVerpflichtungserklaerung: this.getFoermlicheVerpflichtungserklaerung(formValue),
      passnummer: this.getPassnummer(formValue),
      verwandtschaftsbeziehungBeiEUFreizuegigkeit: this.getVerwandschaftsbeziehungBeiEuFreizuegigkeit(formValue),
      abweichenderVerpflichtungsgeber: this.getAbweichenderVerpflichtungsgeber(formValue),

      organisation: this.getOrganisationFuerReferenzGewerblicheAnmietung(formValue),

      ansprechpartner: this.isNotRechtAufFreizuegigkeitAndNotKeineReferenzPerson(formValue)
        ? this.getAnsprechpartnerFuerReferenzGewerblicheAnmietung(formValue)
        : this.marshallVerwandter(formValue)
    };
  }

  marshallPrivateAnmietung(formValue: any) {
    return {
      referenzArt: this.getReferenzArt(formValue),
      begruedungFuerFehlendeReferenz: this.getBegruendungFuerFehlendeReferenz(formValue),
      foermlicheVerpflichtungserklaerung: this.getFoermlicheVerpflichtungserklaerung(formValue),
      passnummer: this.getPassnummer(formValue),
      verwandtschaftsbeziehungBeiEUFreizuegigkeit: this.getVerwandschaftsbeziehungBeiEuFreizuegigkeit(formValue),
      abweichenderVerpflichtungsgeber: this.getAbweichenderVerpflichtungsgeber(formValue),

      verwandtschaftsbeziehungFreitext: this.get(formValue.referenz.anmietungPrivat.verwandtschaftsverhaeltnis),

      ansprechpartner: this.isNotRechtAufFreizuegigkeitAndNotKeineReferenzPerson(formValue)
        ? this.getAnsprechpartnerFuerReferenzPrivateAnmietung(formValue)
        : this.marshallVerwandter(formValue)
    };
  }

  getBegruendungFuerFehlendeReferenz(formValue: any): any {

    let result: any;

    if (this.get(formValue.zusaetzlicheAngaben) && this.get(formValue.angabenZurPerson.isRechtAufFreizuegigkeit) !== true &&
      formValue.referenz.art === '1' && this.valueListSelection('begruendung_fehlende_referenz', formValue.zusaetzlicheAngaben.begruedungFuerFehlendeReferenz)) {
      result = this.valueListSelection('begruendung_fehlende_referenz', formValue.zusaetzlicheAngaben.begruedungFuerFehlendeReferenz);
    } else {
      result = undefined;
    }
    return result;
  }

  getFoermlicheVerpflichtungserklaerung(formValue: any): any {

    let result: any;

    if (this.get(formValue.zusaetzlicheAngaben) && this.isNotRechtAufFreizuegigkeitAndNotKeineReferenzPerson(formValue)
      && this.isNotRechtAufFreizuegigkeitAndNoAbweichenderVerpflichtungsgeber(formValue) !== true && this.getFormattedDatePickerValue(formValue.zusaetzlicheAngaben.foermlicheVerpflichtungserklaerungDatum)) {
      result = {
        datum: this.getFormattedDatePickerValue(formValue.zusaetzlicheAngaben.foermlicheVerpflichtungserklaerungDatum)
      };
    } else {
      result = undefined;
    }
    return result;
  }

  getPassnummer(formValue: any): any {
    let result: any;

    if (this.get(formValue.angabenZurPerson.isRechtAufFreizuegigkeit) === true) {
      result = this.get(formValue.angabenZurPerson.verwandter.passnummer);
    } else {
      result = undefined;
    }
    return result;
  }

  getVerwandschaftsbeziehungBeiEuFreizuegigkeit(formValue: any): any {

    let result: any;

    if (this.get(formValue.angabenZurPerson.isRechtAufFreizuegigkeit) === true) {
      result = {
        value: this.valueListSelection('verwandtschaftsbeziehung', formValue.angabenZurPerson.verwandter.verwandschaftsbeziehung)
      };
    } else {
      result = undefined;
    }
    return result;
  }

  protected isNotRechtAufFreizuegigkeitAndNoAbweichenderVerpflichtungsgeber(formValue: any): boolean {
    return this.isNotRechtAufFreizuegigkeitAndNotKeineReferenzPerson(formValue) && this.get(formValue.kostenuebernahme.reisekostenUebernahme.organisation) === true;
  }

  getAbweichenderVerpflichtungsgeber(formValue: any): any {
    return this.isNotRechtAufFreizuegigkeitAndNotKeineReferenzPerson(formValue) && this.get(formValue.kostenuebernahme.reisekostenUebernahme.organisation) === true;
  }
}
